/**
 * Global fetch interceptor to handle unauthorized (401) responses.
 * Tries refresh token before logging the user out.
 */

import {
  clearStoredTokens,
  isAuthApiUrl,
  refreshAccessToken,
  withAuthorizationHeader,
} from "./authTokens";

type CleanupCallback = () => void | Promise<void>;

const cleanupCallbacks = new Set<CleanupCallback>();

let isInterceptorEnabled = false;
let isHandlingUnauthorized = false;
let originalFetch: typeof window.fetch | null = null;

export const registerUnauthorizedCleanup = (
  callback: CleanupCallback
): (() => void) => {
  cleanupCallbacks.add(callback);
  return () => {
    cleanupCallbacks.delete(callback);
  };
};

const runCleanupCallbacks = async () => {
  for (const callback of Array.from(cleanupCallbacks)) {
    try {
      await callback();
    } catch (error) {
      console.error("Cleanup callback failed:", error);
    }
  }
};

const clearPersistentStorage = async () => {
  clearStoredTokens();

  const keysToRemove = [
    "i18nextLng",
    "app_language",
    "theme-mode",
    "app_theme",
    "hagd_cart_v1",
    "hagd_bar_cart_v1",
    "hagd_user",
    "hagd_building",
    "hagd_currentBuilding",
    "hagd_user_info_etag",
  ];

  keysToRemove.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove ${key}:`, error);
    }
  });

  try {
    sessionStorage.clear();
  } catch (error) {
    console.error("Failed to clear sessionStorage:", error);
  }

  if (typeof caches !== "undefined") {
    try {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((key) => caches.delete(key)));
    } catch (error) {
      console.error("Failed to clear caches:", error);
    }
  }
};

const handleUnauthorized = async () => {
  if (isHandlingUnauthorized) return;
  isHandlingUnauthorized = true;

  try {
    await runCleanupCallbacks();
    await clearPersistentStorage();
  } finally {
    try {
      window.location.replace("/login");
    } catch (error) {
      console.error("Failed to redirect to login:", error);
    } finally {
      setTimeout(() => {
        isHandlingUnauthorized = false;
      }, 2000);
    }
  }
};

const hasAuthorizationHeader = (headers?: HeadersInit): boolean => {
  if (!headers) return false;

  if (headers instanceof Headers) {
    return headers.has("Authorization") || headers.has("authorization");
  }

  if (Array.isArray(headers)) {
    return headers.some(([key]) => key.toLowerCase() === "authorization");
  }

  return Object.keys(headers).some(
    (key) => key.toLowerCase() === "authorization"
  );
};

const getRequestUrl = (input: RequestInfo | URL): string => {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  return input.url;
};

const getAuthorizationHeader = (headers?: HeadersInit): string | null => {
  if (!headers) return null;

  if (headers instanceof Headers) {
    return headers.get("Authorization") || headers.get("authorization");
  }

  if (Array.isArray(headers)) {
    const entry = headers.find(([key]) => key.toLowerCase() === "authorization");
    return entry?.[1] ?? null;
  }

  const key = Object.keys(headers).find(
    (headerKey) => headerKey.toLowerCase() === "authorization",
  );
  return key ? String(headers[key as keyof typeof headers]) : null;
};

const getRequestHeaders = (
  input: RequestInfo | URL,
  init?: RequestInit,
): HeadersInit | undefined => {
  if (init?.headers) {
    return init.headers;
  }
  if (input instanceof Request) {
    return input.headers;
  }
  return undefined;
};

const isPublicDeviceAuthorization = (headers?: HeadersInit): boolean => {
  const value = getAuthorizationHeader(headers);
  return !!value && value.trim().toLowerCase().startsWith("publicdevice");
};

const shouldIgnore401 = (url: string | Request | URL): boolean => {
  const urlString = getRequestUrl(url);
  const ignoredPatterns = ["/cart/", "/cartBar/", "/register-code/", "/users/invitations/"];
  return ignoredPatterns.some((pattern) => urlString.includes(pattern));
};

export const enableFetchInterceptor = () => {
  if (isInterceptorEnabled) return;
  if (typeof window === "undefined") return;

  originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const response = await originalFetch!(input, init);
    const url = getRequestUrl(input);
    const requestHeaders = getRequestHeaders(input, init);

    if (
      response.status !== 401 ||
      !hasAuthorizationHeader(requestHeaders) ||
      isPublicDeviceAuthorization(requestHeaders) ||
      shouldIgnore401(input) ||
      isAuthApiUrl(url)
    ) {
      return response;
    }

    const newAccessToken = await refreshAccessToken(originalFetch!);
    if (newAccessToken) {
      const retryInit = withAuthorizationHeader(init, newAccessToken);
      return originalFetch!(input, retryInit);
    }

    void handleUnauthorized();
    return response;
  };

  isInterceptorEnabled = true;
};

export const restoreOriginalFetch = () => {
  if (!isInterceptorEnabled || !originalFetch) return;

  window.fetch = originalFetch;
  isInterceptorEnabled = false;
  originalFetch = null;
};
